import { Navigate, Outlet } from "react-router";
import { useAuth } from "../api/auth";
import { userHasAppFunction, type AppFunctionKey } from "../shared/types";
import { CircularProgress, Box } from "@mui/material";
import { useEffect } from "react";

type ProtectedRouteProps = {
    isAllowed?: boolean;
    redirectPath?: string;
    functionKey?: AppFunctionKey | null;
    unauthorizedRedirectPath?: string;
    children?: React.ReactNode;
};

export const ProtectedRoute = ({
    isAllowed = true,
    redirectPath = "/",
    functionKey = null,
    unauthorizedRedirectPath = "/",
    children,
}: ProtectedRouteProps) => {

    const { isloggedIn, user, loading, refreshUser } = useAuth();

    useEffect(() => {
        if (!isloggedIn) return;
        if (functionKey !== null && user == null && !loading) {
            refreshUser().catch(() => { });
        }
    }, [isloggedIn, functionKey, user, loading, refreshUser]);

    if (!isloggedIn) {
        return <Navigate to={redirectPath} replace />;
    }

    // When authenticated via token but user is still being fetched, don't
    // prematurely treat it as "unauthorized".
    if (functionKey !== null && user == null) {
        return loading ? (
            <Box sx={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        ) : (
            <Navigate to={unauthorizedRedirectPath} replace />
        );
    }

    if (!isAllowed || !userHasAppFunction(user, functionKey)) {
        return <Navigate to={unauthorizedRedirectPath} replace />;
    }

    return <>{children ? children : <Outlet />}</>;
};